<template>
  <div id="quick-task-select-task">
    <div v-if="recentTasks?.length > 1" class="recent max-w-2xl mx-auto mb-6">
      <div class="flex items-center gap-2 mb-4 pt-2">
        <i class="pi pi-clock" />
        <span class="font-semibold">{{ $t("wordsRecent") }}</span>
      </div>
      <div class="grid grid-cols-3 gap-2">
        <div
          v-for="recentTask in recentTasks"
          :key="recentTask?.namespace + recentTask.key"
          class="p-2 max-w-md bg-surface-100 dark:bg-surface-700 rounded-md grid grid-cols-[auto_1fr_auto] gap-2 items-center w-full"
          role="button"
          :data-gtm-action="analyticsEvents.QUICK_TASK.quick_task_select_recent"
          @click="setActiveTask(recentTask)"
        >
          <span class="bg-primary rounded-md block w-max h-max p-1">
            <img
              :src="getTaskIcon(recentTask.key, recentTask.icon)"
              class="h-4 w-4 svg-color"
              @error="onImageLoadFailed"
            />
          </span>
          <div class="overflow-hidden">
            <p class="text-sm font-medium">{{ recentTask.label }}</p>
            <p class="text-xs text-surface-500 dark:text-surface-400 truncate">
              {{ recentTask.namespace }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="main-tasks max-w-2xl mx-auto pb-6">
      <div v-if="!category" class="w-full">
        <p v-if="filter && !!filteredGroupedTasks.length" class="mb-1 py-3 font-semibold">
          {{ $t("categories") }}
        </p>
        <TaskList
          :options="filteredGroupedTasks"
          handle-click-externally
          wrapper-class="grid @sm:grid-cols-2"
          item-class="h-full quick-task-content-category"
          force-item-border
          :item-props="{
            'data-gtm-action': analyticsEvents.QUICK_TASK.quick_task_select_category,
          }"
          @task-click="category = $event.key"
          @upgrade="handleBillingUpgradeConfirmation"
        />
        <p v-if="filter && !!filteredTasks?.length" class="mb-1 py-3 font-semibold">
          {{ $t("wordsTasks") }}
        </p>
        <TaskList
          v-if="filter && filteredTasks?.length"
          :options="filteredTasks"
          handle-click-externally
          wrapper-class="grid @sm:grid-cols-2"
          force-item-border
          item-class="h-full quick-task-content-type"
          :item-props="{
            'data-gtm-action': analyticsEvents.QUICK_TASK.quick_task_select_task_type,
          }"
          @task-click="setActiveTask"
          @upgrade="handleBillingUpgradeConfirmation"
        />

        <div v-if="!filteredGroupedTasks.length && !filteredTasks?.length">No tasks</div>
      </div>

      <div v-else class="w-full">
        <!-- {{ activeGroup }} -->
        <div
          class="flex gap-2 w-full overflow-x-scroll no-scrollbar mb-2 duration-300 transition-all"
          :class="taskCategoryConfig || taskTypeConfig ? 'h-8' : 'h-0'"
        >
          <Chip
            v-if="taskCategoryConfig"
            removable
            class="animate-[slide-in-right_0.3s_ease_forwards]"
            :data-gtm-action="analyticsEvents.QUICK_TASK.quick_task_clear_category"
            @remove="clearCategory"
          >
            <span
              class="bg-primary rounded-full w-6 h-6 flex items-center justify-center mr-2 -ml-1"
            >
              <img
                :src="getTaskIcon(taskCategoryConfig.key, taskCategoryConfig.icon)"
                class="h-4 w-4 svg-color"
                @error="onImageLoadFailed"
              />
            </span>
            <span>{{ i18n.fromTaskConfig("title", taskCategoryConfig) }}</span>
          </Chip>
          <Chip
            v-if="taskTypeConfig"
            removable
            class="animate-[slide-in-right_0.3s_ease_forwards]"
            :data-gtm-action="analyticsEvents.QUICK_TASK.quick_task_clear_task_type"
            @remove="clearType"
          >
            <span
              class="bg-primary rounded-full w-6 h-6 flex items-center justify-center mr-2 -ml-1"
            >
              <img
                :src="
                  getTaskIcon(
                    taskTypeConfig.key === 'social-media'
                      ? taskTypeConfig.namespace
                      : taskTypeConfig.key,
                    taskTypeConfig.icon
                  )
                "
                class="h-4 w-4 svg-color"
                @error="onImageLoadFailed"
              />
            </span>
            <span>{{ i18n.fromTaskConfig("title", taskTypeConfig) }}</span>
          </Chip>
        </div>
        <div
          v-for="(option, index) in activeGroup?.children"
          :key="`option-${index}`"
          class=""
        >
          <div
            v-if="option.children.length"
            class="mb-1 py-3 font-semibold text-base inline-block"
            @click="type = option.key"
          >
            <div
              :class="{ 'cursor-pointer hover:text-primary': !type }"
              class="flex items-center transition-all gap-x-1 hover:gap-x-2"
            >
              <span>{{ i18n.fromTaskConfig("title", option) }}</span>
              <i v-if="!type" class="pi pi-chevron-right ml-auto shrink-0 text-xs" />
            </div>
          </div>

          <TaskList
            :options="option.children"
            handle-click-externally
            wrapper-class="grid @sm:grid-cols-2"
            force-item-border
            item-class="h-full"
            icon-class="h-7! w-7! p-1.5!"
            :item-props="{
              'data-gtm-action': analyticsEvents.QUICK_TASK.quick_task_select_task_type,
            }"
            @task-click="setActiveTask"
            @upgrade="handleBillingUpgradeConfirmation"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TaskOptionTask } from "@/types/init";
import TaskList from "@/components/newTask/TaskList.vue";
import Chip from "@/volt/Chip.vue";
import Fuse from "fuse.js";

const props = defineProps<{
  filter: string;
  recentTaskKeys?: string[];
  beforeSelect?: () => void;
  afterSelect?: () => void;
}>();

const category = ref("");
const type = ref("");
const { user } = useCurrentUser();
const { handleBillingUpgradeConfirmation } = useUpgradePlanBillingLink(user);
const { filter } = toRefs(props);
const { currentTaskKey } = useQuickTask();
const i18n = withI18nHelpers();
const {
  getTaskByName,
  getTaskIcon,
  data,
  getCategoryByName,
  getTypeByName,
  taskFallbackIcon: fallbackIcon,
} = useTaskConfigDataCore();

const groupedTasks = computed(() =>
  data.value ? formatTaskTree(data.value).filter(c => c.enabled) : []
);

const taskCategoryConfig = computed(() => {
  if (!category.value) return null;
  return getCategoryByName(category.value);
});

const taskTypeConfig = computed(() => {
  if (!type.value) return null;
  return getTypeByName(type.value);
});

const filteredGroupedTasks = computed(() => {
  if (!filter.value) return groupedTasks.value;
  return filterResults(groupedTasks.value);
});

const filteredTasks = computed(() => {
  if (!filter.value || !data.value?.tasks.length) return null;
  return filterResults(sortList(data.value.tasks));
});

const activeGroup = computed(() => {
  if (!category.value) return null;
  const group = groupedTasks.value.find(c => c.key === category.value);
  if (!group) return null;
  const children = group.children.filter(t => t.enabled);
  if (!type.value) {
    group.children = children;
    return {
      ...group,
      children: children.map(task => {
        return {
          ...task,
          children: filterResults(sortList(task.children)),
        };
      }),
    };
  } else {
    const newChildren = children.map(task => {
      return {
        ...task,
        children: filterResults(
          sortList(
            task.children.filter(c => c.namespace === `${category.value}/${type.value}`)
          )
        ),
      };
    });
    return {
      ...group,
      children: newChildren,
    };
  }
});

const recentTasks = computed(
  () =>
    (props.recentTaskKeys
      ?.map(key => getTaskByName(key))
      .filter(Boolean) as TaskOptionTask[]) || []
);

function onImageLoadFailed(e: Event) {
  if (!e.target) return;
  (e.target as HTMLImageElement).src = fallbackIcon;
}

function setActiveTask(task: TaskOptionTask) {
  if (props.beforeSelect) props.beforeSelect();
  const fullKey = `${task.namespace}/${task.key}`;
  currentTaskKey.value = fullKey;
  if (props.afterSelect) props.afterSelect();
}

function clearCategory() {
  category.value = "";
  type.value = "";
}

function clearType() {
  type.value = "";
}

function sortList(tasks: TaskOptionTask[] = []) {
  return [...tasks]
    .filter(t => t.enabled && t.visible)
    .sort((a, b) => {
      return (b.priority || 0) - (a.priority || 0);
    });
}

function filterResults<T = Record<string, any>>(list: T[]) {
  if (!filter.value) {
    return list;
  }
  const fuse = new Fuse(list, {
    keys: ["label", "content.en.title", "content.es.title", "key"],
    // threshold: 0.2,
  });

  const res = fuse.search(filter.value);
  return res.map(({ item }) => item);
}
</script>

<style>
body[data-p-unselectable-text="true"] .task-dialog-root {
  transition: none;
}
.task-dialog-root {
  transition: all 300ms ease-in-out;
}
</style>
